import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { MedChronJobRead } from './schemas'
import { ArtifactVersionRead } from '@/artifacts/schemas'

/**
 * Legacy chronology reports state (hard cutover): the list survives only to back the
 * download-only Legacy Reports page. The living chronology lives in medChronAssetState.
 */
export type MedChronJobState = {
  jobs: { [id: string]: MedChronJobRead }
  lastFetch: number | null
  jobsLoading: boolean
  jobsError: boolean
  // Artifact version state (report downloads)
  latestArtifactVersion: { [jobId: string]: ArtifactVersionRead }
  latestArtifactVersionLoading: { [jobId: string]: boolean }
  latestArtifactVersionError: { [jobId: string]: boolean }
}

const initialState: MedChronJobState = {
  jobs: {},
  lastFetch: null,
  jobsLoading: false,
  jobsError: false,
  latestArtifactVersion: {},
  latestArtifactVersionLoading: {},
  latestArtifactVersionError: {},
}

export const medChronJobSlice = createSlice({
  name: 'medChronJobState',
  initialState,
  reducers: {
    // ============== Listener Actions ============== >
    getMedChronJobs: (_state) => {
      // Handled by the listener middleware
    },
    getLatestArtifactVersion: (_state, _action: PayloadAction<string>) => {
      // Handled by the listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    setJobsLoading: (state, action: PayloadAction<boolean>) => {
      state.jobsLoading = action.payload
    },

    setJobsError: (state, action: PayloadAction<boolean>) => {
      state.jobsError = action.payload
    },

    updateLastFetch: (state) => {
      state.lastFetch = Date.now()
    },

    upsertMedChronJobs: (state, action: PayloadAction<MedChronJobRead[]>) => {
      const records = action.payload.reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {} as Record<string, MedChronJobRead>)

      state.jobs = {
        ...state.jobs,
        ...records,
      }
    },

    setLatestArtifactVersionLoading: (state, action: PayloadAction<{ jobId: string; isLoading: boolean }>) => {
      state.latestArtifactVersionLoading[action.payload.jobId] = action.payload.isLoading
    },

    setLatestArtifactVersionError: (state, action: PayloadAction<{ jobId: string; hasError: boolean }>) => {
      state.latestArtifactVersionError[action.payload.jobId] = action.payload.hasError
    },

    setLatestArtifactVersion: (state, action: PayloadAction<{ jobId: string; artifactVersion: ArtifactVersionRead }>) => {
      state.latestArtifactVersion[action.payload.jobId] = action.payload.artifactVersion
    },
  },
})

// Actions
export const MedChronJobActions = medChronJobSlice.actions

export default medChronJobSlice.reducer
